import { useState } from "react";
import * as Sentry from "@sentry/nextjs";
import toast from "react-hot-toast";

import * as Track from "@qoohoo/common/utils/analytics/track";
import httpf from "../../../utils/frontend/httpf";
import useStore from "../../../store/store";
import useSessionStore from "../../../store/sessionStore";
import { openPayment } from "../../../library/payment";
import { MAIL_SCHEME } from "../../../constants/constants";
import { MultiCurrency } from "@qoohoo/common/utils/currency/index";

const MeetingDetailsModal = ({
  channelDetails,
  closeModal,
  setModalOpen,
  setShowThanksModal,
}) => {
  const [formData, setFormData] = useState({
    user_email: "",
    user_display_name: "",
    agenda: "",
  });

  const userData = useSessionStore((state) => state.userData);
  const setReadyToPay = useStore((state) => state.setReadyToPay);

  const areGuestsAllowed = channelDetails.allow_guests;

  const trackDetails = {};

  const confirmBooking = (ev) => {
    ev.preventDefault();

    const emailRegex = MAIL_SCHEME;

    if (!formData.user_email.match(emailRegex)) {
      return toast.error("Please enter a valid email");
    }

    const payload = {
      uid: channelDetails.uid,
      frontend_amount: channelDetails.channel_fee,
      platform: "web",
      guest_emails: "",
      booking_date: channelDetails.booking_date,
      start_at: channelDetails.start_at,
      ...formData,
    };

    httpf
      .post(`/api/meeting/book-slot/?id=${channelDetails.uid}`, { ...payload })
      .then((res) => {
        const customData = {
          name: channelDetails.name,
          category: channelDetails.category[0].name,
          user_mobile: `+${userData.countryCode}${userData.mobile}`,
          user_email: userData.email,
        };

        openPayment({
          orderId: res.data?.order_id,
          customData,
          closeModal,
          setReadyToPay,
          creatorId: channelDetails?.creator?.uid
        })
          .then(() => {
            setModalOpen(true);
            setShowThanksModal(true);
            // Track.success("subscription_bundle_modal.result", trackDetails);
          })
          .catch((err) => {
            console.error(err);
            Sentry.captureException(err);
            toast.error("Payment failed. Please try again");
            // Track.error("subscription_bundle_modal.result", trackDetails);
          })
          .finally(() => {});
      })
      .catch((err) => {
        const errorMessage = err.message.includes("code")
          ? "Payment failed! Please try again"
          : err.message;

        console.error(err);
        // Track.error("subscription_bundle_modal.result", trackDetails);
        Sentry.captureException(err);
        toast.error(errorMessage);
      })
      .finally(() => {
        closeModal();
        setReadyToPay(false);
      });
  };

  const handleOnChange = (ev) => {
    const field = ev.target.id;

    setFormData((state) => ({ ...state, [field]: ev.target.value }));
  };

  return (
    <div className="sm:inline-block sm:align-middle h-screen sm:h-full">
      <div className="relative inline-flex items-end justify-center w-full h-full sm:items-center font-nunito sm:py-14">
        <div className="relative bg-white sm:rounded-sm max-w-full w-full h-full sm:max-w-[344px] sm:w-[344px]">
          <img
            src="/assets/icons/cancel.svg"
            lt="close button"
            onClick={closeModal}
            className="absolute z-10 cursor-pointer select-none right-5 top-6"
          />
          <form className="w-full h-full py-10 px-5 sm:py-6">
            <div className="mt-[48px] flex flex-col gap-6 text-black text-left">
              <div>
                <label
                  className="font-nunito font-semibold text-sm leading-[24px]"
                  htmlFor="user_display_name"
                >
                  Tell us your name
                </label>
                <input
                  value={formData.user_display_name}
                  onChange={handleOnChange}
                  className="w-full mt-1 py-2 border-b border-[#97999A] placeholder-[#BEBFC0] placeholder-shown:text-sm placeholder-shown:font-nunito text-sm outline-none focus:border-black"
                  type="text"
                  id="user_display_name"
                  placeholder="Johny Bravo"
                />
              </div>
              <div>
                <label
                  className="font-nunito font-semibold text-sm leading-[24px]"
                  htmlFor="user_email"
                >
                  What is your email address
                </label>
                <input
                  value={formData.user_email}
                  onChange={handleOnChange}
                  className="w-full mt-1 py-2 border-b border-[#97999A] placeholder-[#BEBFC0] placeholder-shown:text-sm placeholder-shown:font-nunito text-sm outline-none focus:border-black"
                  type="email"
                  id="user_email"
                  placeholder="iamcool@gmail.com"
                />
              </div>
              {areGuestsAllowed && (
                <button className="py-3 px-6 w-max bg-[#EBEEF1] hover:bg-[#D8DEE4] border border-[#EBEEF1] hover:border-[#D8DEE4] focus:border-black transition-colors duration-300 text-sm font-nunito font-bold rounded-full">
                  Add guests
                </button>
              )}
              <div>
                <label
                  className="font-nunito font-semibold text-sm leading-[24px]"
                  htmlFor="agenda"
                >
                  Add notes for the creator before meeting
                </label>
                <textarea
                  value={formData.agenda}
                  onChange={handleOnChange}
                  className="w-full mt-1 py-2 border-b border-[#97999A] placeholder-[#BEBFC0] placeholder-shown:text-sm placeholder-shown:font-nunito text-sm outline-none focus:border-black resize-none"
                  id="agenda"
                  placeholder="Please share anything that will help prepare for our meeting."
                />
              </div>

              <button
                type="submit"
                onClick={confirmBooking}
                disabled={!formData.user_display_name || !formData.user_email}
                className="bg-[#605FE7] text-white disabled:opacity-40 py-3 mt-[32px] font-nunito font-bold leading-[20px] rounded-full"
              >
                <span className="hidden md:block">Pay to Book</span>
                <span className="md:hidden">
                  Pay {`${MultiCurrency.formatAmountWithSymbol(channelDetails.channel_fee, channelDetails.currency)}`}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MeetingDetailsModal;
